class ContextService {
  constructor() {
    this.currentSession = null;
    this.sessions = this.loadSessions();
    // 최근 세션을 기본 활성 세션으로 설정 (없으면 새로 생성)
    if (this.sessions && this.sessions.length > 0) {
      const sorted = this.sessions.slice().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      this.currentSession = sorted[0];
    } else {
      this.createSession();
    }
  }

  // 세션 생성
  createSession(title = null) {
    const sessionId = `session_${Date.now()}`;
    const session = {
      id: sessionId,
      title: title || `대화 ${this.sessions.length + 1}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messages: [],
      documentContent: '',
      documentHistory: []
    };
    
    this.currentSession = session;
    this.sessions.push(session);
    this.saveSessions();
    
    return session;
  }

  // 현재 세션 가져오기
  getCurrentSession() {
    return this.currentSession;
  }

  // 세션 설정
  setCurrentSession(sessionId) {
    this.currentSession = this.sessions.find(s => s.id === sessionId);
    return this.currentSession;
  }

  // 새 대화 시작
  startNewConversation() {
    this.currentSession = null;
    return this.createSession();
  }

  // 메시지 추가
  addMessage(message) {
    if (!this.currentSession) {
      this.createSession();
    }
    
    const messageWithId = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };
    
    this.currentSession.messages.push(messageWithId);
    this.currentSession.updatedAt = new Date().toISOString();
    this.saveSessions();
    
    return messageWithId;
  }

  // 세션 목록 반환 (복사본)
  listSessions() {
    return this.getSessions();
  }

  // 새 세션 생성 (UI용)
  newSession(title) {
    return this.createSession(title);
  }

  // 세션 선택
  selectSession(sessionId) {
    return this.setCurrentSession(sessionId);
  }

  // 문서 내용 업데이트
  updateDocumentContent(content) {
    if (!this.currentSession) {
      this.createSession();
    }
    
    // 이전 내용을 히스토리에 저장
    if (this.currentSession.documentContent !== content) {
      this.currentSession.documentHistory.push({
        content: this.currentSession.documentContent,
        timestamp: new Date().toISOString()
      });
    }
    
    this.currentSession.documentContent = content;
    this.currentSession.updatedAt = new Date().toISOString();
    this.saveSessions();
    
    // 자동 저장 (로컬 스토리지)
    this.autoSaveDocument(content);
  }

  // 자동 저장
  autoSaveDocument(content) {
    try {
      const autoSaveData = {
        content: content,
        timestamp: new Date().toISOString(),
        sessionId: this.currentSession ? this.currentSession.id : null
      };
      localStorage.setItem('ai_agent_autosave', JSON.stringify(autoSaveData));
    } catch (error) {
      console.error('자동 저장 실패:', error);
    }
  }

  // 자동 저장된 문서 불러오기
  loadAutoSavedDocument() {
    try {
      const autoSaveData = localStorage.getItem('ai_agent_autosave');
      if (autoSaveData) {
        const data = JSON.parse(autoSaveData);
        return data.content || '';
      }
    } catch (error) {
      console.error('자동 저장된 문서 불러오기 실패:', error);
    }
    return '';
  }

  // 문서 내용 가져오기
  getDocumentContent() {
    return this.currentSession ? this.currentSession.documentContent : '';
  }

  // 대화 컨텍스트 가져오기 (AI에게 전달할 컨텍스트)
  getConversationContext(mode) {
    if (!this.currentSession) {
      return {
        messages: [],
        documentContent: '',
        documentSummary: ''
      };
    }

    // 모드별 메시지 필터링 후 최근 10개 메시지만 포함
    const scoped = Array.isArray(this.currentSession.messages)
      ? this.currentSession.messages.filter(m => !mode || m.mode === mode)
      : [];
    const recentMessages = scoped.slice(-10);
    
    // 문서 요약 생성
    const documentSummary = this.generateDocumentSummary(this.currentSession.documentContent);
    
    return {
      messages: recentMessages,
      documentContent: this.currentSession.documentContent,
      documentSummary: documentSummary,
      sessionTitle: this.currentSession.title
    };
  }

  // 문서 요약 생성
  generateDocumentSummary(content) {
    if (!content || content.trim().length === 0) {
      return '문서가 비어있습니다.';
    }

    // 간단한 문서 요약 로직
    const lines = content.split('\n').filter(line => line.trim().length > 0);
    const headings = lines.filter(line => line.startsWith('#'));
    const firstParagraph = lines.find(line => !line.startsWith('#') && line.trim().length > 50);
    
    let summary = '';
    
    if (headings.length > 0) {
      summary += `주요 제목: ${headings.slice(0, 3).map(h => h.replace(/#+\s*/, '')).join(', ')}\n`;
    }
    
    if (firstParagraph) {
      summary += `내용 개요: ${firstParagraph.substring(0, 100)}...`;
    }
    
    return summary || '문서 내용이 있습니다.';
  }

  // 세션 목록 가져오기
  getSessions() {
    return this.sessions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }

  // 세션 삭제
  deleteSession(sessionId) {
    this.sessions = this.sessions.filter(s => s.id !== sessionId);
    if (this.currentSession && this.currentSession.id === sessionId) {
      this.currentSession = null;
    }
    this.saveSessions();
  }

  // 세션 제목 업데이트
  updateSessionTitle(sessionId, title) {
    const session = this.sessions.find(s => s.id === sessionId);
    if (session) {
      session.title = title;
      session.updatedAt = new Date().toISOString();
      this.saveSessions();
    }
  }

  // 세션 저장
  saveSessions() {
    try {
      // 세션 크기 제한 (최대 50개 세션 유지)
      if (this.sessions.length > 50) {
        this.sessions = this.sessions.slice(-50);
      }
      
      // 각 세션의 메시지 수도 제한 (최대 100개 메시지)
      this.sessions.forEach(session => {
        if (session.messages && session.messages.length > 100) {
          session.messages = session.messages.slice(-100);
        }
      });
      
      localStorage.setItem('ai_agent_sessions', JSON.stringify(this.sessions));
    } catch (error) {
      console.error('세션 저장 실패:', error);
      // 저장 실패 시 세션을 더 많이 정리
      if (this.sessions.length > 20) {
        this.sessions = this.sessions.slice(-20);
        try {
          localStorage.setItem('ai_agent_sessions', JSON.stringify(this.sessions));
        } catch (retryError) {
          console.error('세션 재저장도 실패:', retryError);
          // 최후의 수단: 세션 초기화
          this.sessions = [];
        }
      }
    }
  }

  // 세션 불러오기
  loadSessions() {
    try {
      const saved = localStorage.getItem('ai_agent_sessions');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('세션 불러오기 실패:', error);
      return [];
    }
  }

  // 컨텍스트 기반 AI 프롬프트 생성
  generateContextualPrompt(userInput, mode, outputLang = 'ko') {
    const context = this.getConversationContext();
    
    // 언어별 프롬프트 생성
    const prompts = this.getLanguagePrompts(context, mode, outputLang);
    
    return prompts[outputLang] || prompts['ko'];
  }

  // 언어별 프롬프트 반환
  getLanguagePrompts(context, mode, outputLang) {
    const prompts = {
      'ko': this.getKoreanPrompts(context, mode),
      'en': this.getEnglishPrompts(context, mode),
      'zh': this.getChinesePrompts(context, mode),
      'ja': this.getJapanesePrompts(context, mode)
    };
    
    return prompts;
  }

  // 한국어 프롬프트
  getKoreanPrompts(context, mode) {
    if (mode === 'write') {
      return `당신은 전문적인 학술 작가이자 연구 보고서 작성 전문가입니다. 

현재 대화 컨텍스트:
- 세션: ${context.sessionTitle || '새 대화'}
- 기존 문서 요약: ${context.documentSummary}

기존 문서가 있다면, 그 내용을 파악하고 일관성 있게 추가 내용을 작성해주세요.
새로운 내용은 기존 문서의 스타일, 톤, 구조와 일치해야 합니다.

다음 지침을 따라주세요:
1. **기존 문서 분석**: 현재 문서의 구조, 스타일, 톤을 파악
2. **일관성 유지**: 기존 내용과 자연스럽게 연결되는 내용 작성
3. **상세하고 전문적**: 최소 2000-3000단어 이상의 상세한 내용
4. **구조적 완성도**: 목차, 서론, 본문, 결론 등 체계적 구조
5. **구체적 예시**: 실제 사례, 데이터, 통계 등 포함
6. **학술적 톤**: 전문적이고 객관적인 문체 사용

또한 사용자가 학습 자료/자료집 생성을 요청할 수 있습니다. 아래 요구가 포함되면 해당 형식으로 생성하세요.
- 단어장(예: TOEFL/수능/토익): 표 형태로 [단어 | 품사 | 의미 | 예문 | 동의어/반의어 | 암기팁 | 빈도/레벨(CEFR)]
- 기출/모의문제 세트: 섹션(듣기/읽기/어휘/문법 등), 문항 번호, 난이도, 시간 배분, 정답/해설 별도 섹션 포함
- 학습 워크시트/퀴즈: 학습목표, 소요시간, 준비물, 활동 단계, 정답지 별도 제공
- 보고서/서류/양식: 표지(제목/작성자/일자), 초록, 목차, 본문 섹션, 참고문헌, 부록
- 학습 계획표: 주차/일자별 목표, 콘텐츠, 과제, 평가 기준, 체크박스

가능하면 구성요소를 명확한 제목/표/목록으로 구조화하고, 필요한 곳에 표를 사용하세요.

또한 아래와 같은 문서/보고서/서식 유형 요청이 오면, 적합한 섹션과 표 템플릿을 포함해 구조화해 주세요(해당 없으면 생략 가능):
- 연구/학술: 연구보고서, 학술논문 초안, 문헌고찰(리뷰), 메타분석 개요, 실험계획서, IRB 서류, 동의서, 연구윤리/윤리선언
- 프로젝트/제품: PRD(제품 요구사항 문서), 기능명세서(SRS), 기술설계서(Design Doc), 로드맵, 마일스톤/간트 개요, 변경요청서/변경이력
- 품질/테스트: 테스트 계획/전략, 테스트 케이스/시나리오 표, 버그 리포트, QA 종합 리포트, 품질 지표 대시보드 개요
- 사업/경영: 사업계획서, 전략기획서, 시장조사 요약, SWOT/3C/포터5력 분석, 재무계획/손익추정, KPI 보드, 리스크 레지스터
- 제안/입찰: 제안요청서(RFP) 응답서, 견적서, 제안서 요약, 경쟁사 비교표, 일정/예산/범위 표
- 회의/업무: 회의안건/회의록/액션아이템, 업무보고(주간/월간), 브리핑 노트, 의사결정 기록, 메모/요청서, 인수인계서
- 법무/정책: 계약서 초안(항목별), 업무협약서(MOU), 개인정보처리방침, 보안정책, 내부규정/지침(SOP), 컴플라이언스 체크리스트
- 인사/채용: 이력서/CV, 자기소개서/커버레터, 채용공고, 면접질문지, 평가표, 온보딩 가이드
- 마케팅/커뮤니케이션: 보도자료, 캠페인 브리프, 페르소나/여정맵 요약, 콘텐츠 캘린더, SNS/블로그 가이드라인, FAQ
- 고객/제품지원: 사용자 매뉴얼, 빠른 시작 가이드, 릴리스노트, 문제해결 가이드, SLA 개요, 서비스 공지
- 재무/구매: 예산서, 정산서, 세부 항목표, 인보이스/영수증, 구매요청서/발주서, 비용보고서
- 교육: 강의계획서(시러버스), 수업안/학습지도안, 평가루브릭, 출결/성적표 양식, 실험/실습 보고서 템플릿, 수업자료(워크시트/퀴즈)
- 데이터/연구지원: 설문지, 변수정의서/데이터사전, 코드북, 데이터 수집/정리 체크리스트, 인용/참고문헌 서식
- 경영지원: 일정표/타임라인, 체크리스트, 위험평가서, 사고/인시던트 리포트, 감사보고서, 개선계획서(CAP)

각 문서는 다음 공통 구조를 우선 고려합니다: 표지(제목/작성자/일자), 개요/초록, 목차, 본문 섹션(배경/목표/방법/결과/논의 등 문서 성격에 맞춤), 결론/액션아이템, 참고문헌/부록. 표, 다단 목록, 코드/수식, 그림 캡션이 필요하면 포함하세요.

한국어로 응답해주세요.`;
    } else if (mode === 'edit') {
      return `당신은 전문적인 학술 편집자이자 문서 개선 전문가입니다.

현재 대화 컨텍스트:
- 세션: ${context.sessionTitle || '새 대화'}
- 기존 문서 요약: ${context.documentSummary}

기존 문서의 내용을 파악하고, 사용자의 요청에 따라 전문적으로 수정하고 개선해주세요.

다음 원칙을 따라주세요:
1. **기존 문서 이해**: 현재 문서의 구조, 스타일, 내용을 완전히 파악
2. **일관성 유지**: 기존 문서의 톤과 스타일을 유지하면서 수정
3. **내용 보강**: 부족한 부분을 상세하게 보완
4. **구조 개선**: 논리적이고 체계적인 구조로 재편성
5. **전문성 강화**: 학술적이고 전문적인 표현으로 개선
6. **완성도 높임**: 전체적으로 완성도 높은 문서로 개선

사용자가 학습 자료 생성(단어장, 문제세트, 워크시트, 보고서/양식 등)을 의도한 경우, 해당 형식의 구조를 제안하거나 필요한 섹션/표를 추가하여 문서를 보강하세요. 표는 컬럼을 명확히 지정하고, 정답/해설은 본문과 분리하세요.

또한 아래 유형으로의 개편/정리 요청이 있을 수 있습니다. 해당 유형에 맞는 섹션 체계/표준 용어/필수 항목을 반영해 구조, 흐름, 가독성을 개선하세요: 연구보고서/논문, PRD/SRS/설계서, 테스트 계획/케이스, 사업계획/전략보고, RFP 응답/제안서, 회의록/업무보고, 계약/정책/SOP, 보도자료/브리프, 사용자 매뉴얼/릴리스노트, 예산/인보이스/구매문서, 강의계획/학습지도안/루브릭, 설문/데이터사전/참고문헌, 위험/감사/인시던트 보고서 등.

한국어로 응답해주세요.`;
    } else if (mode === 'question') {
      return `당신은 전문적인 연구 컨설턴트이자 학술 자문가입니다.

현재 대화 컨텍스트:
- 세션: ${context.sessionTitle || '새 대화'}
- 기존 문서 요약: ${context.documentSummary}

기존 문서의 내용을 바탕으로 사용자의 질문에 대해 상세하고 전문적인 답변을 제공해주세요.

다음 원칙을 따라주세요:
1. **문서 내용 활용**: 기존 문서의 내용을 참고하여 답변
2. **상세한 분석**: 질문을 다각도로 분석하고 깊이 있는 답변 제공
3. **구체적 예시**: 실제 사례와 구체적인 데이터 포함
4. **전문적 관점**: 학술적이고 전문적인 시각에서 접근
5. **실용적 조언**: 실행 가능한 구체적인 방안 제시
6. **체계적 구성**: 논리적이고 체계적인 답변 구조

한국어로 응답해주세요.`;
    }
    return '';
  }

  // 영어 프롬프트
  getEnglishPrompts(context, mode) {
    if (mode === 'write') {
      return `You are a professional academic writer and research report specialist.

Current conversation context:
- Session: ${context.sessionTitle || 'New conversation'}
- Existing document summary: ${context.documentSummary}

If there is an existing document, understand its content and write additional content consistently.
New content should match the existing document's style, tone, and structure.

Please follow these guidelines:
1. **Analyze existing document**: Understand the current document's structure, style, and tone
2. **Maintain consistency**: Write content that naturally connects with existing content
3. **Be detailed and professional**: Write at least 2000-3000 words in detail
4. **Structural completeness**: Systematic structure including table of contents, introduction, body, and conclusion
5. **Concrete examples**: Include actual cases, data, and statistics
6. **Academic tone**: Use professional and objective writing style

Additionally, the user may request learning materials or document kits. If such intent is present, produce structured outputs accordingly:
- Vocabulary lists (e.g., TOEFL): table with [Word | POS | Meaning | Example | Synonyms/Antonyms | Mnemonic | Frequency/Level (CEFR)]
- Past/mock exam sets: sections (listening/reading/vocab/grammar), numbered questions, difficulty, time allocation, separate Answer Key with explanations
- Study worksheets/quizzes: learning objectives, duration, materials, step-by-step activities, separate answer sheet
- Reports/forms/templates: cover (title/author/date), abstract, TOC, main sections, references, appendix
- Study plans: weekly/daily goals, content, assignments, assessment criteria, checkboxes

Use clear headings, tables, and lists where appropriate.

Please respond in English.`;
    } else if (mode === 'edit') {
      return `You are a professional academic editor and document improvement specialist.

Current conversation context:
- Session: ${context.sessionTitle || 'New conversation'}
- Existing document summary: ${context.documentSummary}

Understand the existing document's content and professionally modify and improve it according to the user's request.

Please follow these principles:
1. **Understand existing document**: Fully comprehend the current document's structure, style, and content
2. **Maintain consistency**: Modify while maintaining the existing document's tone and style
3. **Enhance content**: Supplement insufficient parts in detail
4. **Improve structure**: Reorganize into a logical and systematic structure
5. **Strengthen professionalism**: Improve with academic and professional expressions
6. **Increase completeness**: Improve the overall document completeness

Please respond in English.`;
    } else if (mode === 'question') {
      return `You are a professional research consultant and academic advisor.

Current conversation context:
- Session: ${context.sessionTitle || 'New conversation'}
- Existing document summary: ${context.documentSummary}

Based on the existing document's content, provide detailed and professional answers to the user's questions.

Please follow these principles:
1. **Utilize document content**: Answer by referencing the existing document's content
2. **Detailed analysis**: Analyze questions from multiple angles and provide in-depth answers
3. **Concrete examples**: Include actual cases and specific data
4. **Professional perspective**: Approach from an academic and professional viewpoint
5. **Practical advice**: Provide specific executable plans
6. **Systematic structure**: Logical and systematic answer structure

Please respond in English.`;
    }
    return '';
  }

  // 중국어 프롬프트
  getChinesePrompts(context, mode) {
    if (mode === 'write') {
      return `你是一位专业的学术作者和研究报告专家。

当前对话上下文：
- 会话：${context.sessionTitle || '新对话'}
- 现有文档摘要：${context.documentSummary}

如果有现有文档，请理解其内容并一致地编写额外内容。
新内容应与现有文档的风格、语气和结构相匹配。

请遵循以下准则：
1. **分析现有文档**：了解当前文档的结构、风格和语气
2. **保持一致性**：编写与现有内容自然连接的内容
3. **详细且专业**：至少写2000-3000字的详细内容
4. **结构完整性**：包括目录、引言、正文和结论的系统结构
5. **具体示例**：包括实际案例、数据和统计
6. **学术语调**：使用专业和客观的写作风格

请用中文回答。`;
    } else if (mode === 'edit') {
      return `你是一位专业的学术编辑和文档改进专家。

当前对话上下文：
- 会话：${context.sessionTitle || '新对话'}
- 现有文档摘要：${context.documentSummary}

理解现有文档的内容，并根据用户的要求专业地进行修改和改进。

请遵循以下原则：
1. **理解现有文档**：完全理解当前文档的结构、风格和内容
2. **保持一致性**：在保持现有文档的语气和风格的同时进行修改
3. **增强内容**：详细补充不充分的部分
4. **改进结构**：重新组织成逻辑和系统的结构
5. **加强专业性**：用学术和专业的表达来改进
6. **提高完整性**：提高整体文档的完整性

请用中文回答。`;
    } else if (mode === 'question') {
      return `你是一位专业的研究顾问和学术顾问。

当前对话上下文：
- 会话：${context.sessionTitle || '新对话'}
- 现有文档摘要：${context.documentSummary}

基于现有文档的内容，对用户的问题提供详细和专业的回答。

请遵循以下原则：
1. **利用文档内容**：参考现有文档的内容来回答
2. **详细分析**：从多个角度分析问题并提供深入的答案
3. **具体示例**：包括实际案例和具体数据
4. **专业观点**：从学术和专业的角度来处理
5. **实用建议**：提供具体的可执行计划
6. **系统结构**：逻辑和系统的答案结构

请用中文回答。`;
    }
    return '';
  }

  // 일본어 프롬프ト
  getJapanesePrompts(context, mode) {
    if (mode === 'write') {
      return `あなたは専門的な学術作家であり、研究報告書の専門家です。

現在の会話コンテキスト：
- セッション：${context.sessionTitle || '新しい会話'}
- 既存ドキュメントの概要：${context.documentSummary}

既存のドキュメントがある場合、その内容を理解し、一貫性のある追加コンテンツを作成してください。
新しいコンテンツは、既存ドキュメントのスタイル、トーン、構造と一致する必要があります。

以下のガイドラインに従ってください：
1. **既存ドキュメントの分析**：現在のドキュメントの構造、スタイル、トーンを把握
2. **一貫性の維持**：既存のコンテンツと自然に接続されるコンテンツを作成
3. **詳細かつ専門的**：最低2000-3000語以上の詳細なコンテンツ
4. **構造的な完全性**：目次、序論、本文、結論などの体系的構造
5. **具体的な例**：実際のケース、データ、統計を含む
6. **学術的トーン**：専門的で客観的な文体を使用

日本語で回答してください。`;
    } else if (mode === 'edit') {
      return `あなたは専門的な学術編集者であり、ドキュメント改善の専門家です。

現在の会話コンテキスト：
- セッション：${context.sessionTitle || '新しい会話'}
- 既存ドキュメントの概要：${context.documentSummary}

既存のドキュメントの内容を理解し、ユーザーの要求に従って専門的に変更および改善してください。

以下の原則に従ってください：
1. **既存ドキュメントの理解**：現在のドキュメントの構造、スタイル、内容を完全に把握
2. **一貫性の維持**：既存ドキュメントのトーンとスタイルを維持しながら変更
3. **コンテンツの強化**：不十分な部分を詳細に補完
4. **構造の改善**：論理的で体系的な構造に再編成
5. **専門性の強化**：学術的で専門的な表現で改善
6. **完成度の向上**：全体的に完成度の高いドキュメントに改善

日本語で回答してください。`;
    } else if (mode === 'question') {
      return `あなたは専門的な研究コンサルタントであり、学術アドバイザーです。

現在の会話コンテキスト：
- セッション：${context.sessionTitle || '新しい会話'}
- 既存ドキュメントの概要：${context.documentSummary}

既存のドキュメントの内容に基づいて、ユーザーの質問に対して詳細かつ専門的な回答を提供してください。

以下の原則に従ってください：
1. **ドキュメント内容の活用**：既存のドキュメントの内容を参照して回答
2. **詳細な分析**：複数の角度から質問を分析し、深い回答を提供
3. **具体的な例**：実際のケースと具体的なデータを含む
4. **専門的な視点**：学術的で専門的な視点からアプローチ
5. **実用的なアドバイス**：実行可能な具体的な計画を提供
6. **体系的構成**：論理的で体系的な回答構造

日本語で回答してください。`;
    }
    return '';
  }
}

// 싱글톤 인스턴스 생성
const contextService = new ContextService();
export default contextService;
